import Phaser from 'phaser';
import { MorseNoteSprite } from './MorseNote';
import { messageToNotes, type MorseSymbol, type MorseNote } from '@/utils/morse';
import { MORSE_SPACING, computePixelHitZones, type DifficultyConfig, type PixelHitZones } from '@/utils/timing';

export interface NoteTrackConfig {
  message: string;
  difficulty: DifficultyConfig;
  hitZoneX: number;
  trackY: number;
  mask?: Phaser.Display.Masks.GeometryMask;
}

export class NoteTrack {
  private scene: Phaser.Scene;
  private notes: MorseNoteSprite[] = [];
  private noteQueue: MorseNote[] = [];
  private config: NoteTrackConfig;
  private nextNoteIndex: number = 0;
  private isPlaying: boolean = false;

  // Position-based tracking
  private scrollOffset: number = 0;  // pixels scrolled since start
  private noteSpawnOffsets: number[] = [];  // virtual scroll position for each note
  private hitZones: PixelHitZones;

  constructor(scene: Phaser.Scene, config: NoteTrackConfig) {
    this.scene = scene;
    this.config = config;

    // Parse message into notes
    this.noteQueue = messageToNotes(config.message);

    // Compute pixel hit zones from difficulty
    this.hitZones = computePixelHitZones(config.difficulty);

    // Calculate spawn offsets for all notes
    this.calculateSpawnOffsets();
  }

  private calculateSpawnOffsets(): void {
    // Distance from hit zone to right edge of track
    const trackRightEdge = this.scene.cameras.main.width - 40;
    const distanceToTrackEdge = trackRightEdge - this.config.hitZoneX;

    // Position notes by their left edge
    // First note's left edge starts at the track edge
    let leftEdge = distanceToTrackEdge;

    for (let i = 0; i < this.noteQueue.length; i++) {
      const note = this.noteQueue[i];
      const prevNote = i > 0 ? this.noteQueue[i - 1] : null;

      if (prevNote) {
        // Determine the gap to add after the previous note
        let gap: number = MORSE_SPACING.symbolGap;

        if (note.charIndex !== prevNote.charIndex) {
          // New letter - use letter gap plus padding for timing affordance
          const letterPadding = MORSE_SPACING.dotWidth * 0.5;  // Half unit extra
          gap = MORSE_SPACING.letterGap + letterPadding;

          // Check for word break (gap in char indices means there was a space)
          if (note.charIndex > prevNote.charIndex + 1) {
            gap = MORSE_SPACING.wordGap;
          }
        }

        // Advance by the previous note's width plus the gap
        const prevWidth = prevNote.symbol === '.' ? MORSE_SPACING.dotWidth : MORSE_SPACING.dashWidth;
        leftEdge += prevWidth + gap;
      }

      this.noteSpawnOffsets.push(leftEdge);
    }
  }

  /**
   * Start the note track
   */
  start(_gameTime: number): void {
    this.isPlaying = true;
    this.scrollOffset = 0;
  }

  /**
   * Update the track - spawn notes and manage active notes
   */
  update(_time: number, delta: number): void {
    if (!this.isPlaying) return;

    // Advance the scroll position
    this.scrollOffset += this.config.difficulty.scrollSpeed * (delta / 1000);

    // Spawn notes that are about to come on screen
    this.spawnUpcomingNotes();

    // Update all active notes
    for (const note of this.notes) {
      note.update(_time, delta);
    }

    // Mark notes as missed if they've passed the ok zone
    this.checkMissedNotes();

    // Clean up passed notes
    this.cleanupPassedNotes();
  }

  /**
   * Check for notes that should be marked missed (position-based)
   */
  private checkMissedNotes(): void {
    for (const note of this.notes) {
      if (note.shouldMarkMissed()) {
        note.state = 'missed';
      }
    }
  }

  private spawnUpcomingNotes(): void {
    const screenRight = this.scene.cameras.main.width;
    const spawnBuffer = 50;  // pixels beyond right edge to spawn
    const spawnThreshold = this.scrollOffset + screenRight + spawnBuffer;

    while (this.nextNoteIndex < this.noteQueue.length) {
      const noteOffset = this.noteSpawnOffsets[this.nextNoteIndex];

      if (noteOffset > spawnThreshold) {
        break;
      }

      const noteData = this.noteQueue[this.nextNoteIndex];

      // Calculate spawn X position based on how far we've scrolled
      const spawnX = this.config.hitZoneX + (noteOffset - this.scrollOffset);

      const noteSprite = new MorseNoteSprite(this.scene, {
        symbol: noteData.symbol,
        charIndex: noteData.charIndex,
        symbolIndex: noteData.symbolIndex,
        isLastInChar: noteData.isLastInChar,
        expectedChar: noteData.expectedChar,
        spawnX: spawnX,
        speed: this.config.difficulty.scrollSpeed,
        hitZoneX: this.config.hitZoneX,
        trackY: this.config.trackY,
        hitZones: this.hitZones,
      });

      // Apply mask to clip notes at track boundaries
      if (this.config.mask) {
        noteSprite.setMask(this.config.mask);
      }

      this.notes.push(noteSprite);
      this.nextNoteIndex++;
    }
  }

  private cleanupPassedNotes(): void {
    this.notes = this.notes.filter(note => {
      if (note.hasPassed() && note.state !== 'hit') {
        note.markPassed();
        return false;
      }
      return note.active;
    });
  }

  /**
   * Get the next note in the hit zone that matches the input
   */
  getNextHittableNote(symbol: MorseSymbol): MorseNoteSprite | null {
    for (const note of this.notes) {
      if (note.state === 'approaching' || note.state === 'in_zone') {
        if (note.symbol === symbol) {
          return note;
        }
        // If there's a note of wrong type first, return null (wrong input)
        return null;
      }
    }
    return null;
  }

  /**
   * Get the next note that should be hit (regardless of symbol)
   */
  getNextNote(): MorseNoteSprite | null {
    for (const note of this.notes) {
      if (note.state === 'approaching' || note.state === 'in_zone') {
        return note;
      }
    }
    return null;
  }

  /**
   * Check if all notes have been processed
   */
  isComplete(): boolean {
    return this.nextNoteIndex >= this.noteQueue.length && this.notes.length === 0;
  }

  /**
   * Get total number of notes in the level
   */
  getTotalNotes(): number {
    return this.noteQueue.length;
  }

  /**
   * Get number of notes spawned so far
   */
  getSpawnedNotes(): number {
    return this.nextNoteIndex;
  }

  /**
   * Get estimated level duration in pixels (scroll distance)
   */
  getDuration(): number {
    if (this.noteSpawnOffsets.length === 0) return 0;
    return this.noteSpawnOffsets[this.noteSpawnOffsets.length - 1] + 200;  // Add buffer
  }

  /**
   * Destroy the track and all notes
   */
  destroy(): void {
    for (const note of this.notes) {
      note.destroy();
    }
    this.notes = [];
    this.isPlaying = false;
  }
}
